import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportFilter } from '../../types';
import { isFilterActive } from '../../hooks/useFilteredReports';
import { StatusLegendTooltip } from '../StatusLegendTooltip';
import './ReportFilters.css';

interface ReportFiltersProps {
  filter: ReportFilter;
  onFilterChange: (filter: ReportFilter) => void;
  onReset?: () => void;
  actionButton?: ReactNode;
}

export const ReportFilters: React.FC<ReportFiltersProps> = ({ filter, onFilterChange, onReset, actionButton }) => {
  const { t } = useTranslation();

  const handleReportNumberChange = (reportNumber: string): void => {
    onFilterChange({ ...filter, reportNumber });
  };

  const handleCustomerNameChange = (customerName: string): void => {
    onFilterChange({ ...filter, customerName });
  };

  const handleTechnicianChange = (technicianName: string): void => {
    onFilterChange({ ...filter, technicianName });
  };

  const handleMachineNameChange = (machineName: string): void => {
    onFilterChange({ ...filter, machineName });
  };

  const handleSparePartNameChange = (sparePartNumber: string): void => {
    onFilterChange({ ...filter, sparePartName: sparePartNumber });
  };

  const handleStatusToggle = (statusKey: keyof ReportFilter, value: boolean): void => {
    onFilterChange({ ...filter, [statusKey]: value });
  };

  return (
    <div className="filters-section">
      <fieldset className="search-filters">
        <legend>{t('mainForm.searchFilters')}</legend>
        <div className="search-filters-group">
          <input
            type="text"
            placeholder={t('reportTable.number')}
            value={filter.reportNumber}
            onChange={(e) => handleReportNumberChange(e.target.value)}
            className="filter-input"
            aria-label={t('mainForm.filterByReportNumber')}
          />
          <input
            type="text"
            placeholder={t('reportTable.customer')}
            value={filter.customerName}
            onChange={(e) => handleCustomerNameChange(e.target.value)}
            className="filter-input"
            aria-label={t('mainForm.filterByCustomerName')}
          />
          <input
            type="text"
            placeholder={t('reportTable.technician')}
            value={filter.technicianName}
            onChange={(e) => handleTechnicianChange(e.target.value)}
            className="filter-input"
            aria-label={t('mainForm.filterByTechnician')}
          />
          <input
            type="text"
            placeholder={t('mainForm.machineName')}
            value={filter.machineName}
            onChange={(e) => handleMachineNameChange(e.target.value)}
            className="filter-input"
            aria-label={t('mainForm.filterByMachineName')}
          />
          <input
            type="text"
            placeholder={t('mainForm.sparePartNumber')}
            value={filter.sparePartName}
            onChange={(e) => handleSparePartNameChange(e.target.value)}
            className="filter-input"
            aria-label={t('mainForm.filterBySparePart')}
          />
        </div>
        {onReset && isFilterActive(filter) && (
          <div className="search-filters-actions">
            <button
              type="button"
              onClick={onReset}
              className="btn-reset-filters"
              aria-label={t('mainForm.resetFilters')}
            >
              <span className="material-icons">filter_alt_off</span>
              {t('mainForm.resetFilters')}
            </button>
          </div>
        )}
      </fieldset>

      <div className="filters-row">
        <fieldset className="status-filters">
          <legend>
            {t('mainForm.statusFilter')}
            <StatusLegendTooltip />
          </legend>
          <div className="status-toggle-group">
            <button
              type="button"
              className={`status-toggle status-toggle-new ${filter.showNew ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showNew', !filter.showNew)}
              aria-label={t('mainForm.showNewReports')}
              aria-pressed={filter.showNew}
            >
              {t('status.NEW')}
            </button>
            <button
              type="button"
              className={`status-toggle status-toggle-ready ${filter.showReady ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showReady', !filter.showReady)}
              aria-label={t('mainForm.showReadyReports')}
              aria-pressed={filter.showReady}
            >
              {t('status.READY')}
            </button>
            <button
              type="button"
              className={`status-toggle status-toggle-in-progress ${filter.showInProgress ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showInProgress', !filter.showInProgress)}
              aria-label={t('mainForm.showInProgressReports')}
              aria-pressed={filter.showInProgress}
            >
              {t('status.IN_PROGRESS')}
            </button>
            <button
              type="button"
              className={`status-toggle status-toggle-signed ${filter.showSigned ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showSigned', !filter.showSigned)}
              aria-label={t('mainForm.showSignedReports')}
              aria-pressed={filter.showSigned}
            >
              {t('status.SIGNED')}
            </button>
            <button
              type="button"
              className={`status-toggle status-toggle-closed ${filter.showClosed ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showClosed', !filter.showClosed)}
              aria-label={t('mainForm.showClosedReports')}
              aria-pressed={filter.showClosed}
            >
              {t('status.CLOSED')}
            </button>
            <button
              type="button"
              className={`status-toggle status-toggle-canceled ${filter.showCanceled ? 'active' : ''}`}
              onClick={() => handleStatusToggle('showCanceled', !filter.showCanceled)}
              aria-label={t('mainForm.showCanceledReports')}
              aria-pressed={filter.showCanceled}
            >
              {t('status.CANCELED')}
            </button>
          </div>
        </fieldset>
        {actionButton && (
          <div className="filters-action">
            {actionButton}
          </div>
        )}
      </div>
    </div>
  );
};
